<script setup lang="ts">
import { useLocale } from '@/plugins/translation';
import BookCover from '@/components/bookshelf/BookCover.vue';
import type { BookV1 } from '@ilteducation/content-registry-types';
import { capitalize, computed, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import BookPreviewDrawer from '@/components/bookshelf/BookPreviewDrawer.vue';
import BookActionButtons from '@/components/bookshelf/BookActionButtons.vue';
import { BookWithRelatedContents } from '@ilteducation/videos';
import bookDeepLinkUrl from '@/composables/use-book-deeplink';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { DialogCloseButton } from '@ilteducation/components';

const props = withDefaults(
  defineProps<{
    books: BookWithRelatedContents[];
    preferredLanguage?: string;
  }>(),
  {
    books: () => [],
    preferredLanguage: 'sv',
  },
);

const model = defineModel({
  default: false,
});
const { t, l } = useLocale();
const { smAndUp } = useDisplay();

type LangKey = keyof typeof l.languages;
const filteredBooks = computed(() =>
  props.books.filter(({ languages }) => {
    const sec = languages?.secondaryLanguage;
    const main = languages.mainLanguage;

    // pass if: no secondary (valid main), or both secondary & main are valid
    return (!sec && l.languages[main as LangKey]) || (l.languages[sec as LangKey] && l.languages[main as LangKey]);
  }),
);

const selectedBookLanguage = ref<BookWithRelatedContents>();

const sortedBooks = computed(() =>
  filteredBooks.value.toSorted((a, b) => {
    if (a.languages.secondaryLanguage && b.languages.secondaryLanguage) {
      return t(l.languages[a.languages.secondaryLanguage as keyof typeof l.languages] as string).localeCompare(
        t(l.languages[b.languages.secondaryLanguage as keyof typeof l.languages] as string),
      );
    }
    if (a.languages.secondaryLanguage) {
      return t(l.languages[a.languages.secondaryLanguage as keyof typeof l.languages] as string).localeCompare(
        t(l.languages[a.languages.mainLanguage as keyof typeof l.languages] as string),
      );
    }
    if (b.languages.secondaryLanguage) {
      return t(l.languages[a.languages.mainLanguage as keyof typeof l.languages] as string).localeCompare(
        t(l.languages[b.languages.secondaryLanguage as keyof typeof l.languages] as string),
      );
    }
    return a.title > b.title ? 1 : -1;
  }),
);

watch(
  sortedBooks,
  (res) => {
    if (props.preferredLanguage === 'sv') {
      selectedBookLanguage.value = res.find(
        (book) => book.languages?.mainLanguage === props.preferredLanguage && !book.languages?.secondaryLanguage,
      );
      if (selectedBookLanguage.value) {
        return;
      }
    }
    const preselectedLanguage = res.find(
      (book) => book.languages.secondaryLanguage && book.languages.secondaryLanguage === props.preferredLanguage,
    );
    if (preselectedLanguage) {
      selectedBookLanguage.value = preselectedLanguage;
    } else if (res.length) {
      selectedBookLanguage.value = res[0];
    }
  },
  { immediate: true },
);

const bookLanguage = (book: BookV1) =>
  l.languages[(book.languages.secondaryLanguage ?? book.languages.mainLanguage) as keyof typeof l.languages];

const bookTitle = computed(() => selectedBookLanguage.value?.title || selectedBookLanguage.value?.originalTitle);
const { xs } = useDisplay();
</script>

<template>
  <BookPreviewDrawer
    v-if="xs && selectedBookLanguage"
    :book="selectedBookLanguage"
    :bookInAllLanguages="sortedBooks"
    v-model="model"
    @close="model = false"
    @selectBook="(book) => (selectedBookLanguage = book)"
  ></BookPreviewDrawer>
  <v-dialog
    v-if="!xs && model && filteredBooks.length && selectedBookLanguage"
    v-model="model"
    content-class="align-center"
    max-width="940"
    aria-labelledby="book_title"
  >
    <v-card min-width="min(720px, 95vw)" max-width="940" class="rounded-lg">
      <v-card-text class="d-flex flex-column align-center py-8 px-12 gr-6">
        <DialogCloseButton @click="model = false" />
        <v-row class="gc-12 w-100">
          <v-col cols="12" sm="4" md="3" class="d-flex flex-column align-center align-sm-start">
            <BookCover
              class="d-flex justify-center justify-sm-start align-self-center"
              playable
              :book="selectedBookLanguage"
              :zoom-on-hover="false"
            />
            <BookActionButtons :book="selectedBookLanguage"></BookActionButtons>
            <v-sheet class="d-flex flex-column gr-3 mt-4">
              <div v-if="selectedBookLanguage.authors">
                <span class="h6">Författare</span>
                <p v-for="author in selectedBookLanguage.authors" :key="`${author.firstName} ${author.lastName}`">
                  {{ `${author.firstName} ${author.lastName}` }}
                </p>
              </div>
              <div v-if="selectedBookLanguage.publisher.name">
                <span class="h6">Utgivare</span>
                <p>{{ selectedBookLanguage.publisher.name }}</p>
              </div>
            </v-sheet>
          </v-col>
          <v-col cols="12" sm="7" md="8">
            <v-card elevation="0">
              <div class="d-flex pl-4" :class="[{ 'w-75': smAndUp }]">
                <v-select
                  v-if="filteredBooks.length > 1"
                  v-model="selectedBookLanguage"
                  density="comfortable"
                  variant="outlined"
                  class="mb-1"
                  rounded="md"
                  item-value="id"
                  :item-title="(item) => capitalize(t(bookLanguage(item) as string))"
                  :items="sortedBooks"
                  hide-details
                  return-object
                >
                  <template #prepend-inner>
                    <FontAwesomeIcon icon="fa-regular fa-globe"></FontAwesomeIcon>
                  </template>
                </v-select>
              </div>
              <v-card-title id="book_title" class="text-h6">{{ bookTitle }}</v-card-title>
              <v-row justify="center">
                <v-col cols="12">
                  <div class="d-flex justify-center"></div>
                </v-col>
              </v-row>
              <v-card-text class="book-description pb-0 d-flex flex-column gr-4">
                <div
                  :style="`
                    overflow-y: scroll;
                    max-height: ${selectedBookLanguage.items ? '250px' : '350px'};
                  `"
                  v-html="selectedBookLanguage.description"
                ></div>
                <div v-if="selectedBookLanguage.items" class="d-flex flex-column">
                  <h5 id="heading-related-content" class="text-h6">Relaterat innehåll</h5>
                  <v-list class="w-100" aria-labelledby="heading-related-content">
                    <v-list-item
                      v-for="i in selectedBookLanguage.items"
                      :key="i.id"
                      target="_blank"
                      :href="bookDeepLinkUrl(selectedBookLanguage, i)"
                      :title="(i.metadata?.title || '') as string"
                    >
                      <template #prepend>
                        <FontAwesomeIcon class="mr-2" icon="fa-regular fa-link" />
                      </template>
                      <template #append>
                        <FontAwesomeIcon icon="fa-regular fa-chevron-right"></FontAwesomeIcon>
                      </template>
                    </v-list-item>
                  </v-list>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
:deep(.book-description) span,
:deep(.book-description) p {
  font-size: 1rem !important;
  font-weight: 400;
}
</style>
